import { Theme } from '@mui/material/styles'

/**
 * Centralized form field styles
 */

/**
 * Standard input/textfield styles
 */
export const inputStyles = (theme: Theme) => ({
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgb(38, 47, 67)'
    : 'rgb(245, 245, 245)',
  borderRadius: '4px',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(0, 0, 0, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(22, 28, 42, 0.3)'
        : 'rgba(0, 0, 0, 0.05)',
    },
  },
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
    '&::placeholder': {
      color: theme.palette.text.tertiary,
      opacity: 1,
    },
  },
})

/**
 * Disabled input styles
 */
export const disabledInputStyles = (theme: Theme) => ({
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgb(47, 53, 65)'
    : 'rgba(0, 0, 0, 0.05)',
  cursor: 'not-allowed',
  opacity: 0.6,
  '& .MuiInputBase-input': {
    cursor: 'not-allowed',
  },
})

/**
 * Form label styles
 */
export const formLabelStyles = (theme: Theme) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  marginBottom: '8px',
  color: theme.palette.text.secondary,
  '&.Mui-focused': {
    color: theme.palette.primary.main,
  },
  '&.Mui-error': {
    color: theme.palette.text.error,
  },
})

/**
 * Form helper text styles
 */
export const helperTextStyles = (theme: Theme) => ({
  fontSize: '0.75rem',
  marginTop: '4px',
  color: theme.palette.text.tertiary,
  '&.Mui-error': {
    color: theme.palette.text.error,
  },
})

/**
 * Select/Autocomplete styles
 */
export const selectStyles = (theme: Theme) => ({
  ...inputStyles(theme),
  '& .MuiSelect-select': {
    paddingRight: '32px',
  },
  '& .MuiSelect-icon': {
    color: theme.palette.text.secondary,
  },
})

/**
 * Form container/grid styles
 */
export const formContainerStyles = (_theme: Theme) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '16px',
  padding: '20px',
})

/**
 * Form row/field group styles (for horizontal layouts)
 */
export const formRowStyles = () => ({
  display: 'flex',
  gap: '16px',
  alignItems: 'flex-start',
  '& > *': {
    flex: 1,
  },
})

/**
 * Form section styles (for grouped fields)
 */
export const formSectionStyles = (theme: Theme) => ({
  padding: '16px',
  borderRadius: '4px',
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.02)'
    : 'rgba(0, 0, 0, 0.02)',
  border: `1px solid ${theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.08)'
    : 'rgba(0, 0, 0, 0.08)'}`,
})

/**
 * Checkbox/Radio styles
 */
export const checkboxStyles = (theme: Theme) => ({
  color: theme.palette.text.secondary,
  '&.Mui-checked': {
    color: theme.palette.primary.main,
  },
  '&.Mui-disabled': {
    color: theme.palette.text.disabled,
  },
})

/**
 * Switch styles
 */
export const switchStyles = (theme: Theme) => ({
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked': {
      color: theme.palette.primary.main,
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.primary.main,
        opacity: 0.5,
      },
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.3)'
      : 'rgba(0, 0, 0, 0.3)',
  },
})
